// Competitor claims reviewed against Google documentation on 2026-10-07.
// Keep feature comparisons here so product and competitor changes are easy to review.
export const comparisonPath = '/compare/reportwerks-vs-looker-studio';
export const comparisonMetadata = {
  title: 'ReportWerks vs Google Looker Studio | Marketing Reporting Comparison',
  description: 'Compare ReportWerks and Google Looker Studio for marketing, fundraising, advertising, email, analytics and cross-channel reporting. Compare setup, connectors, BigQuery, automation and maintenance.',
};
export const comparisonRows = [
  [
    "Primary purpose",
    "Complete marketing and fundraising reporting platform",
    "Dashboard and visualization builder"
  ],
  [
    "Starting price",
    "From $100/month",
    "Free version available"
  ],
  [
    "Setup required",
    "Connect and authorize supported accounts; confirm account mappings",
    "Simple for straightforward Google reports; more setup as sources and requirements grow"
  ],
  [
    "Dashboard building",
    "Prebuilt marketing, fundraising, and campaign reporting experiences",
    "Flexible report editor with templates; configure dashboards around your needs"
  ],
  [
    "Data infrastructure",
    "Managed reporting for supported integrations",
    "Choose and maintain sources, connectors, and any upstream infrastructure"
  ],
  [
    "BigQuery required?",
    "No",
    "Not always; may be useful for advanced, historical or consolidated reporting"
  ],
  [
    "BigQuery billing",
    "No separate BigQuery account required for normal ReportWerks reporting",
    "BigQuery connections require Google Cloud billing and can generate usage/query charges"
  ],
  [
    "Database knowledge required",
    "No for standard integrations",
    "Can become necessary for advanced reporting"
  ],
  [
    "SQL knowledge required",
    "No for normal reporting",
    "Often useful when using BigQuery or custom datasets"
  ],
  [
    "Data warehouse management",
    "No client-managed warehouse needed for standard reporting",
    "Customer or agency manages a warehouse when one is used"
  ],
  [
    "Multiple marketing platforms",
    "Built for cross-platform reporting",
    "Possible, but each source must be connected and configured"
  ],
  [
    "Fundraising / nonprofit reporting",
    "Connected donor, donation, and campaign reporting for supported platforms",
    "Model fundraising data through connectors, exports, or prepared datasets"
  ],
  [
    "Google Ads",
    "Integrated",
    "Native connector available"
  ],
  [
    "GA4",
    "Integrated",
    "Native connector available"
  ],
  [
    "Meta / Facebook Ads",
    "Integrated",
    "Usually requires a third-party/community connector or prepared data source"
  ],
  [
    "Microsoft / Bing Ads",
    "Integrated",
    "Usually requires an external connector or prepared data source"
  ],
  [
    "LinkedIn Ads",
    "Integrated",
    "Typically requires an external connector or prepared data source"
  ],
  [
    "Email marketing platforms",
    "Supported platforms feed into ReportWerks",
    "Frequently requires third-party connectors or intermediate data"
  ],
  [
    "Fundraising platforms",
    "Supports fundraising/donor reporting integrations",
    "Usually requires custom/community connectors, exports, or a data warehouse"
  ],
  [
    "Cross-source normalization",
    "Cross-channel reporting with platform-specific metric context",
    "Design blends, calculations, or upstream transformations"
  ],
  [
    "Historical data storage",
    "Historical reporting and date comparisons; source coverage varies",
    "Depends on source retention, extracts, or warehouse configuration"
  ],
  [
    "Blending multiple data sources",
    "Built into the reporting platform/data model",
    "Supported, but blends need to be created and maintained"
  ],
  [
    "Data refresh",
    "Automated by ReportWerks",
    "Depends on the individual source and connector"
  ],
  [
    "Refresh/query costs",
    "Automated updates within plan limits; specialized integrations may cost extra",
    "Connectors and external systems can add costs; BigQuery has free usage limits"
  ],
  [
    "Scheduled reporting",
    "Automated scheduled reports on Professional and above",
    "Scheduled PDF delivery available; limits vary by offering"
  ],
  [
    "Weekly/monthly/quarterly reports",
    "Weekly, monthly, and quarterly automation on Growth and above",
    "Configure reports and delivery schedules; limits vary by offering"
  ],
  [
    "AI reporting assistance",
    "AI-assisted insights; report assistance varies by plan",
    "Gemini assistance and conversational analytics depend on feature, subscription, and enablement"
  ],
  [
    "Client dashboard",
    "Purpose-built client reporting environment",
    "Reports can be shared with viewers"
  ],
  [
    "Client management",
    "Accounts and sources mapped to client reporting profiles",
    "Organize reports and data sources; Pro offers team workspaces"
  ],
  [
    "User permissions",
    "Plan-based user access and team reporting",
    "Google sharing; Pro adds organizational controls"
  ],
  [
    "Data source credentials",
    "User-authorized, client-scoped integrations",
    "Owner, viewer, or service-account credentials depending on connector"
  ],
  [
    "Connector maintenance",
    "ReportWerks maintains supported integrations; users may need to reauthorize",
    "Customer, agency, or connector vendor; responsibilities vary"
  ],
  [
    "Third-party connector fees",
    "Standard supported connections within plan allowances; add-on/custom charges may apply",
    "Some community connectors have recurring fees"
  ],
  [
    "Custom calculations",
    "Reporting metrics and configurations for supported sources",
    "Flexible calculated fields"
  ],
  [
    "Custom visualization flexibility",
    "Curated for reporting consistency",
    "Excellent — stronger for fully custom, one-off visualization work"
  ],
  [
    "Ad-hoc dashboard creation",
    "Focused on supported ReportWerks reporting",
    "Excellent"
  ],
  [
    "Technical maintenance",
    "Low",
    "Moderate to high for sophisticated reporting environments"
  ],
  [
    "Best for",
    "Organizations wanting reporting that works across marketing and fundraising systems with minimal infrastructure management",
    "Analysts and teams willing to build and maintain their own BI/reporting environment"
  ]
];
export const potentialRequirements = [
  'Paid third-party connectors', 'BigQuery or another data warehouse',
  'Storage and query usage beyond free limits', 'ETL and data synchronization services',
  'Analyst or developer time', 'Dashboard construction', 'Metric normalization',
  'Connector troubleshooting', 'Credential maintenance', 'New connector development',
  'Ongoing client and account administration',
];
export const comparisonFaqs = [
  { question: 'Do I need BigQuery with Looker Studio?', answer: 'Not necessarily. Looker Studio connects directly to Google Ads, Google Analytics, Google Sheets, and other supported sources. BigQuery may be useful for centralized history, large datasets, custom SQL, and transformations before visualization; it is not universally required.' },
  { question: 'Is Looker Studio free?', answer: 'A no-cost version is available. Paid connectors, warehouse usage, and the time spent building and maintaining advanced reporting can add costs. Simple dashboards may need none of these extras.' },
  { question: 'Are scheduled reports included in every ReportWerks plan?', answer: 'Automated scheduled reports are included on Professional and above. Growth and above include automated weekly, monthly, and quarterly reporting. Plans start at $100 per month; see the pricing page for current allowances and add-ons.' },
];
export const comparisonSources = [
  ['Product name and no-cost version', 'https://docs.cloud.google.com/data-studio/welcome'],
  ['Google and community connectors', 'https://docs.cloud.google.com/data-studio/available-connectors'],
  ['BigQuery connection and billing', 'https://docs.cloud.google.com/data-studio/connect-to-google-bigquery'],
  ['BigQuery costs and free usage', 'https://cloud.google.com/bigquery/pricing'],
  ['Scheduled report delivery', 'https://docs.cloud.google.com/data-studio/schedule-automatic-report-delivery'],
  ['Gemini features and requirements', 'https://docs.cloud.google.com/data-studio/gemini-overview'],
];

